import { extendTheme, type Theme } from '@mui/joy/styles';
import type { JarvisUiThemeTokens } from './tokens';
import { DEFAULT_UI_THEME_TOKENS } from './tokens';

function shade(hex: string, amount: number): string {
  const raw = hex.replace('#', '');
  const full = raw.length === 3
    ? raw.split('').map((c) => c + c).join('')
    : raw.padEnd(6, '0').slice(0, 6);
  const num = Number.parseInt(full, 16);
  if (!Number.isFinite(num)) return hex;
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  const r = clamp(((num >> 16) & 255) * (1 + amount));
  const g = clamp(((num >> 8) & 255) * (1 + amount));
  const b = clamp((num & 255) * (1 + amount));
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Build a Joy theme from JARVIS UI tokens.
 * Keeps PDF/document branding out of scope — those use pdf_export_config.
 */
export function buildJoyThemeFromTokens(tokens: JarvisUiThemeTokens = DEFAULT_UI_THEME_TOKENS): Theme {
  const primary = tokens.primary;
  const primaryDark = shade(primary, -0.12);
  const primaryLight = shade(primary, 0.35);
  const accent = tokens.accent;
  const accentDark = shade(accent, -0.12);

  return extendTheme({
    fontFamily: {
      body: tokens.fontFamily,
      display: tokens.fontFamily,
    },
    colorSchemes: {
      light: {
        palette: {
          primary: {
            solidBg: primary,
            // Accent shows on CTA hover so the token is visibly used.
            solidHoverBg: accent,
            solidActiveBg: accentDark,
            solidColor: '#FFFFFF',
            solidHoverColor: tokens.hoverText,
            solidActiveColor: tokens.hoverText,
            softBg: `${primary}1A`,
            softColor: primaryDark,
            softHoverBg: tokens.hoverBg,
            softActiveBg: tokens.hoverBg,
            softHoverColor: tokens.hoverText,
            plainColor: primary,
            plainHoverBg: tokens.hoverBg,
            plainHoverColor: tokens.hoverText,
            outlinedBorder: primary,
            outlinedColor: primary,
            outlinedHoverBg: tokens.hoverBg,
            outlinedHoverBorder: tokens.hoverBorder,
            outlinedHoverColor: tokens.hoverText,
            50: primaryLight,
            100: `${primary}22`,
            200: `${primary}44`,
            400: shade(primary, 0.1),
            500: primary,
            600: primaryDark,
            700: shade(primary, -0.2),
          },
          background: {
            body: tokens.backgroundBody,
            surface: tokens.backgroundSurface,
            level1: tokens.backgroundLevel1,
            level2: shade(tokens.backgroundLevel1, -0.03),
            level3: shade(tokens.backgroundLevel1, -0.06),
          },
          text: {
            primary: tokens.textPrimary,
            secondary: tokens.textSecondary,
            tertiary: shade(tokens.textSecondary, 0.25),
          },
          warning: {
            solidBg: accent,
            solidHoverBg: accentDark,
            solidColor: tokens.hoverText,
            softBg: tokens.hoverBg,
            softColor: shade(accent, -0.45),
            softHoverBg: shade(tokens.hoverBg, -0.04),
            plainColor: shade(accent, -0.25),
            outlinedBorder: tokens.hoverBorder,
            outlinedColor: shade(accent, -0.35),
          },
        },
      },
    },
    cssVarPrefix: 'joy',
    components: {
      JoyModal: {
        styleOverrides: {
          backdrop: {
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          },
        },
      },
    },
  });
}

/** CSS custom properties for selectable chrome + non-Joy surfaces. */
export function applyThemeCssVariables(tokens: JarvisUiThemeTokens) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.setProperty('--jarvis-primary', tokens.primary);
  root.style.setProperty('--jarvis-accent', tokens.accent);
  root.style.setProperty('--jarvis-hover-bg', tokens.hoverBg);
  root.style.setProperty('--jarvis-hover-border', tokens.hoverBorder);
  root.style.setProperty('--jarvis-hover-text', tokens.hoverText);
  root.style.setProperty('--jarvis-nav-bg', tokens.navBg);
  root.style.setProperty('--jarvis-nav-text', tokens.navText);
  root.style.setProperty('--jarvis-nav-muted-text', tokens.navMutedText);
  root.style.setProperty('--jarvis-nav-border', tokens.navBorder);
  root.style.setProperty('--jarvis-nav-selected-bg', tokens.navSelectedBg);
  root.style.setProperty('--jarvis-nav-selected-text', tokens.navSelectedText);
  root.style.setProperty('--jarvis-nav-hover-bg', tokens.navHoverBg);
  root.style.setProperty('--jarvis-nav-hover-text', tokens.navHoverText);
  root.style.setProperty('--jarvis-nav-scrollbar-track', tokens.navScrollbarTrack);
  root.style.setProperty('--jarvis-nav-scrollbar-thumb', tokens.navScrollbarThumb);
  root.style.setProperty('--jarvis-nav-scrollbar-thumb-hover', tokens.navScrollbarThumbHover);
  root.style.setProperty('--jarvis-card-icon-bg', tokens.cardIconBg);
  root.style.setProperty('--jarvis-card-icon-color', tokens.cardIconColor);
  root.style.setProperty('--jarvis-chip-bg', tokens.chipBg);
  root.style.setProperty('--jarvis-chip-text', tokens.chipText);
  root.style.setProperty('--jarvis-chip-selected-bg', tokens.chipSelectedBg);
  root.style.setProperty('--jarvis-chip-selected-text', tokens.chipSelectedText);
  root.style.setProperty('--jarvis-chip-outline-bg', tokens.chipOutlineBg);
  root.style.setProperty('--jarvis-chip-outline-text', tokens.chipOutlineText);
  root.style.setProperty('--jarvis-chip-outline-selected-bg', tokens.chipOutlineSelectedBg);
  root.style.setProperty('--jarvis-chip-outline-selected-text', tokens.chipOutlineSelectedText);
  root.style.setProperty('--jarvis-chip-outline-selected-border', tokens.chipOutlineSelectedBorder);
  root.style.setProperty('--jarvis-selection-bg', tokens.selectionBg);
  root.style.setProperty('--jarvis-selection-text', tokens.selectionText);
  // Back-compat aliases from earlier document-row naming.
  root.style.setProperty('--jarvis-doc-row-selected-bg', tokens.selectionBg);
  root.style.setProperty('--jarvis-doc-row-selected-text', tokens.selectionText);
  root.style.setProperty('--jarvis-stage-bg', tokens.stageBg);
  root.style.setProperty('--jarvis-stage-text', tokens.stageText);
  root.style.setProperty('--jarvis-stage-border', tokens.stageBorder);
  root.style.setProperty('--jarvis-stage-selected-bg', tokens.stageSelectedBg);
  root.style.setProperty('--jarvis-stage-selected-text', tokens.stageSelectedText);
  root.style.setProperty('--jarvis-stage-selected-border', tokens.stageSelectedBorder);
  root.style.setProperty('--jarvis-stage-badge-bg', tokens.stageBadgeBg);
  root.style.setProperty('--jarvis-stage-badge-text', tokens.stageBadgeText);
  root.style.setProperty('--jarvis-stage-selected-badge-bg', tokens.stageSelectedBadgeBg);
  root.style.setProperty('--jarvis-stage-selected-badge-text', tokens.stageSelectedBadgeText);
  root.style.setProperty('--jarvis-stage-meta-text', tokens.stageMetaText);
  root.style.setProperty('--jarvis-stage-selected-meta-text', tokens.stageSelectedMetaText);
  root.style.setProperty('--jarvis-stage-meta-hover-text', tokens.stageMetaHoverText);
  root.style.setProperty('--jarvis-bg-body', tokens.backgroundBody);
  root.style.setProperty('--jarvis-bg-surface', tokens.backgroundSurface);
  root.style.setProperty('--jarvis-bg-level1', tokens.backgroundLevel1);
  root.style.setProperty('--jarvis-text-primary', tokens.textPrimary);
  root.style.setProperty('--jarvis-text-secondary', tokens.textSecondary);
  root.style.setProperty('--jarvis-font-family', tokens.fontFamily);
}
